import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Blueprint } from '../../../models/blueprint.model';
import {
  BlueprintItem,
  BlueprintItemWrite,
} from '../../../models/blueprint-item.model';
import {
  QuantityType,
  QuoteItemData,
  timeToQuantity,
} from '../../../models/quote-item-data.model';
import { QuantityPipe } from '../../../pipes/quantity.pipe';
import { consumableSelectionsFromLinks } from '../../../models/consumable.model';

@Injectable()
export class BlueprintItemDataEditorService {
  private readonly quantityPipe = inject(QuantityPipe);
  private _items = signal<QuoteItemData[]>([]);
  public readonly items = this._items.asReadonly();

  /**
   * Loads blueprint items into the editor.
   * Converts all blueprint items (including nested blueprints) to QuoteItemData.
   */
  loadBlueprintItems(blueprint: Blueprint, allBlueprints?: Blueprint[]): void {
    const convertedItems = this.convertBlueprintItemsToQuoteItemData(
      blueprint.items || [],
      null,
      {
        allBlueprints,
        visitedBlueprintIds: new Set([String(blueprint.id)]),
      },
    );
    this._items.set(convertedItems);
  }

  /**
   * Maps a blueprint item's material settings to quote-item fields when expanding
   * a blueprint into the editor tree.
   *
   * Linked items use the linked blueprint's default material (not the parent
   * blueprint's), so nested blueprints keep their own material context — e.g.
   * Pattern (Medium Density Epoxy) inside Composite Mould (CFRP).
   *
   * All other items pass through inherit_material; when true, material_id is
   * left null so the quote tree recalculator can propagate from the parent.
   */
  private resolveBlueprintItemMaterial(
    blueprintItem: BlueprintItem,
    allBlueprints: Blueprint[],
  ): Pick<QuoteItemData, 'material_id' | 'inherit_material'> {
    if (blueprintItem.linked_to_blueprint_id && allBlueprints.length > 0) {
      const linkedBlueprint = allBlueprints.find(
        (blueprint) =>
          String(blueprint.id) === String(blueprintItem.linked_to_blueprint_id),
      );
      if (linkedBlueprint?.default_material_id != null) {
        return {
          material_id: linkedBlueprint.default_material_id,
          inherit_material: false,
        };
      }
    }

    return {
      material_id: blueprintItem.inherit_material
        ? null
        : (blueprintItem.material_id ?? null),
      inherit_material: blueprintItem.inherit_material ?? false,
    };
  }

  /**
   * Converts BlueprintItems to QuoteItemData array.
   * Recursively expands nested blueprints when items reference other blueprints.
   * Creates ref-* items for nested blueprints
   *
   * **What are ref-* items?**
   * ref-* items are temporary, read-only display items created when viewing blueprint contents.
   * They have persistent_ids starting with "ref-" (e.g., "ref-123-0", "ref-123-456-0").
   * They represent items from a blueprint template shown in read-only mode. They allow users to view nested blueprint structures
   * without making them editable. These items are NOT saved to the database - they exist
   * only for display purposes and are created on-demand when a blueprint reference is expanded.
   *
   * @param blueprintItems - The BlueprintItems to convert
   * @param initialParentId - The parent ID to use for root items (null for top-level)
   * @param options - Optional configuration for recursive expansion
   */
  convertBlueprintItemsToQuoteItemData(
    blueprintItems: BlueprintItem[],
    initialParentId: string | null,
    options?: {
      persistentIdPrefix?: string;
      useOriginalSiblingOrder?: boolean;
      allBlueprints?: Blueprint[];
      visitedBlueprintIds?: Set<string>;
    },
  ): QuoteItemData[] {
    if (!blueprintItems || blueprintItems.length === 0) {
      return [];
    }

    const items: QuoteItemData[] = [];
    const idMap = new Map<string, string>(); // BlueprintItem ID -> persistent_id
    const itemDataMap = new Map<string, QuoteItemData>();
    const visitedBlueprintIds =
      options?.visitedBlueprintIds || new Set<string>();

    // Sort items by sibling_order for consistent processing
    const sortedItems = [...blueprintItems].sort(
      (a, b) => a.sibling_order - b.sibling_order,
    );

    // Build a set of item IDs that have linked_to_blueprint_id
    // Children of these items should be skipped as they'll be expanded from the linked blueprint
    const itemsWithLinkedBlueprint = new Set<string>();
    for (const item of sortedItems) {
      if (item.id !== undefined && item.linked_to_blueprint_id) {
        itemsWithLinkedBlueprint.add(String(item.id));
      }
    }

    // First pass: create all items and build ID maps
    // Skip items whose parent has linked_to_blueprint_id to avoid duplication
    for (let idx = 0; idx < sortedItems.length; idx++) {
      const blueprintItem = sortedItems[idx];
      if (blueprintItem.id === undefined) {
        continue;
      }

      // Skip items whose parent has linked_to_blueprint_id
      // These items will be replaced by the expansion of the linked blueprint
      if (
        blueprintItem.parent_blueprint_item_id &&
        itemsWithLinkedBlueprint.has(
          String(blueprintItem.parent_blueprint_item_id),
        )
      ) {
        continue;
      }

      const persistentId = options?.persistentIdPrefix
        ? `${options.persistentIdPrefix}${idx}`
        : this.generatePersistentId();

      const { material_id, inherit_material } =
        this.resolveBlueprintItemMaterial(
          blueprintItem,
          options?.allBlueprints ?? [],
        );

      const quantityType = blueprintItem.quantity_type ?? QuantityType.UNIT;

      const itemData: QuoteItemData = {
        persistent_id: persistentId,
        name: blueprintItem.name,
        quantity: this.quantityPipe.transform(
          { quantity: blueprintItem.quantity, quantity_type: quantityType },
          'input',
        ),
        unit_cost: blueprintItem.unit_cost,
        batch_size: blueprintItem.batch_size ?? undefined,
        markup_percentage: blueprintItem.markup_percentage,
        quantity_type: quantityType,
        material_id,
        inherit_material,
        tax_code_id: blueprintItem.tax_code_id ?? null,
        capex_equipment_ids:
          (blueprintItem.capex_equipment_ids?.length ?? 0) > 0
            ? blueprintItem.capex_equipment_ids
            : (blueprintItem.capex_equipment?.map(
                (equipment) => equipment.id,
              ) ?? []),
        consumables: consumableSelectionsFromLinks(blueprintItem.consumables),
        blueprint_id: blueprintItem.linked_to_blueprint_id || undefined,
        parent_id: null, // Set in second pass
        sibling_order: idx,
        cad_object_ids: [],
        pricing_model_id: blueprintItem.pricing_model_id || undefined,
        source: 'loaded',
      };

      idMap.set(String(blueprintItem.id), persistentId);
      itemDataMap.set(String(blueprintItem.id), itemData);
      items.push(itemData);
    }

    // Second pass: set parent relationships and expand nested blueprints
    for (const blueprintItem of sortedItems) {
      if (blueprintItem.id === undefined) {
        continue;
      }

      const itemData = itemDataMap.get(String(blueprintItem.id));
      if (!itemData) continue;

      // Set parent_id
      if (blueprintItem.parent_blueprint_item_id != null) {
        const parentId = idMap.get(
          String(blueprintItem.parent_blueprint_item_id),
        );
        if (parentId) {
          itemData.parent_id = parentId;
        } else {
          // Invalid parent reference - item becomes root item
          console.warn(
            `BlueprintItem "${blueprintItem.name}" (id: ${blueprintItem.id}) references non-existent parent ${blueprintItem.parent_blueprint_item_id}. Treating as root item.`,
          );
          itemData.parent_id = initialParentId;
        }
      } else {
        itemData.parent_id = initialParentId;
      }

      // Expand nested blueprint if this item references one
      if (
        blueprintItem.linked_to_blueprint_id &&
        options?.allBlueprints?.length
      ) {
        const nestedBlueprintId = String(blueprintItem.linked_to_blueprint_id);

        // Prevent circular references
        if (visitedBlueprintIds.has(nestedBlueprintId)) {
          console.warn(
            `Circular reference detected: blueprint ${nestedBlueprintId} already visited`,
          );
          continue;
        }

        const nestedBlueprint = options.allBlueprints.find(
          (b) => String(b.id) === nestedBlueprintId,
        );

        if (nestedBlueprint?.items?.length) {
          visitedBlueprintIds.add(nestedBlueprintId);

          const nestedItems = this.convertBlueprintItemsToQuoteItemData(
            nestedBlueprint.items,
            itemData.persistent_id,
            {
              persistentIdPrefix: options.persistentIdPrefix
                ? `${options.persistentIdPrefix}${itemData.persistent_id}-`
                : `ref-${itemData.persistent_id}-`,
              useOriginalSiblingOrder: options.useOriginalSiblingOrder,
              allBlueprints: options.allBlueprints,
              visitedBlueprintIds: new Set(visitedBlueprintIds),
            },
          );

          items.push(...nestedItems);
        }
      }
    }

    return items;
  }

  /**
   * Converts QuoteItemData to BlueprintItem array format for sending to backend
   */
  toBlueprintItems(): BlueprintItemWrite[] {
    const allItems = this.items();
    const itemsMap = new Map<string, QuoteItemData>();
    const persistentIdToTempId = new Map<string, string>();
    let tempIdCounter = 1;

    // Build maps
    for (const item of allItems) {
      itemsMap.set(item.persistent_id, item);
      if (!persistentIdToTempId.has(item.persistent_id)) {
        persistentIdToTempId.set(item.persistent_id, tempIdCounter.toString());
        tempIdCounter++;
      }
    }

    const result: BlueprintItemWrite[] = [];
    const savableItems = allItems.filter(
      (item) => !item.persistent_id.startsWith('ref-'),
    );

    // Convert each saved row. ref-* rows are the expanded view of a linked blueprint.
    for (const item of savableItems) {
      const tempId = persistentIdToTempId.get(item.persistent_id)!;
      const parentTempId = item.parent_id
        ? (persistentIdToTempId.get(item.parent_id) ?? null)
        : null;

      let quantity;
      if (item.quantity && item.quantity.includes(':')) {
        const [hrs, mins, secs] = item.quantity
          .split(':')
          .map((str) => parseInt(str, 10));
        quantity = timeToQuantity(hrs || 0, mins || 0, secs || 0);
      } else {
        quantity = parseFloat(item.quantity || '1') || 1.0;
      }

      result.push({
        id: tempId,
        name: item.name?.trim() || `Item ${item.sibling_order + 1}`,
        quantity: quantity,
        unit_cost: item.unit_cost || '0',
        batch_size: item.batch_size ?? null,
        quantity_type: item.quantity_type ?? QuantityType.UNIT,
        markup_percentage: item.markup_percentage || '0',
        material_id: item.inherit_material ? null : item.material_id || null,
        inherit_material: item.inherit_material ?? false,
        tax_code_id: item.tax_code_id ?? null,
        parent_blueprint_item_id: parentTempId,
        sibling_order: item.sibling_order || 1,
        linked_to_blueprint_id: item.blueprint_id, // Map blueprint_id from QuoteItemData to linked_to_blueprint_id for BlueprintItem
        pricing_model_id: item.pricing_model_id || null,
        capex_equipment_ids: item.capex_equipment_ids ?? [],
        consumables: item.consumables ?? [],
      });
    }

    return result;
  }

  /**
   * Sorts siblings by sibling_order
   */
  sortSiblingsBySiblingOrder(items: QuoteItemData[]): QuoteItemData[] {
    return [...items].sort((a, b) => a.sibling_order - b.sibling_order);
  }

  draggingSubject = new BehaviorSubject<QuoteItemData | false>(false);
  public dragging$ = this.draggingSubject.asObservable();
  dragStart(item: QuoteItemData) {
    this.draggingSubject.next(item);
  }
  dragEnd() {
    this.draggingSubject.next(false);
  }
  async drop(
    dropTarget: QuoteItemData,
    position: 'before' | 'endOfList' | 'child',
  ): Promise<void> {
    const draggedItem = this.draggingSubject.value;
    if (!draggedItem) {
      throw new Error('No item is being dragged');
    }

    // Cancel if dropping on itself
    if (dropTarget.persistent_id === draggedItem.persistent_id) {
      this.dragEnd();
      return;
    }

    // Prevent dropping on referenced blueprint items (they're read-only)
    if (dropTarget.persistent_id.startsWith('ref-')) {
      this.dragEnd();
      return;
    }

    // Prevent dropping referenced blueprint items (they're read-only)
    if (draggedItem.persistent_id.startsWith('ref-')) {
      this.dragEnd();
      return;
    }

    // Determine the new parent
    let newParentId: string | null = null;
    if (position === 'child') {
      newParentId = dropTarget.persistent_id;
    } else if (dropTarget.parent_id !== draggedItem.parent_id) {
      newParentId = dropTarget.parent_id;
    } else {
      newParentId = draggedItem.parent_id;
    }

    // Calculate the target index where the dragged item should be placed
    const targetIndex = this.calculateTargetIndex(
      draggedItem,
      dropTarget,
      position,
      newParentId,
    );

    // Get all items and find the dragged item from the current state
    const allItems = this.items();
    const currentDraggedItem = allItems.find(
      (item) => item.persistent_id === draggedItem.persistent_id,
    );
    if (!currentDraggedItem) {
      this.dragEnd();
      return;
    }

    // Get all siblings (excluding dragged item) and sort them
    const siblings = allItems
      .filter(
        (item) =>
          item.parent_id === newParentId &&
          item.persistent_id !== draggedItem.persistent_id,
      )
      .sort((a, b) => a.sibling_order - b.sibling_order);

    // Insert dragged item at the target index with updated parent_id
    siblings.splice(targetIndex, 0, {
      ...currentDraggedItem,
      parent_id: newParentId,
    });

    // Renumber all siblings to be consecutive (0, 1, 2, 3...)
    const updatedItems = allItems.map((item) => {
      const siblingIndex = siblings.findIndex(
        (s) => s.persistent_id === item.persistent_id,
      );
      if (siblingIndex >= 0) {
        // Only update parent_id for the dragged item
        if (item.persistent_id === draggedItem.persistent_id) {
          return {
            ...item,
            sibling_order: siblingIndex,
            parent_id: newParentId,
          };
        }
        return { ...item, sibling_order: siblingIndex };
      }
      return item;
    });

    this._items.set(updatedItems);

    this.dragEnd();
  }

  /**
   * Calculates the target index where the dragged item should be placed.
   * Returns the index in the sorted siblings array (0-based).
   */
  private calculateTargetIndex(
    draggedItem: QuoteItemData,
    dropTarget: QuoteItemData,
    position: 'before' | 'endOfList' | 'child',
    newParentId: string | null,
  ): number {
    const allItems = this.items();

    // Get all siblings at the new parent level (excluding the dragged item)
    const siblings = allItems
      .filter(
        (item) =>
          item.parent_id === newParentId &&
          item.persistent_id !== draggedItem.persistent_id,
      )
      .sort((a, b) => a.sibling_order - b.sibling_order);

    if (position === 'endOfList') {
      // Insert at the end
      return siblings.length;
    }

    if (position === 'before') {
      // Insert before dropTarget: find dropTarget's index
      const dropTargetIndex = siblings.findIndex(
        (s) => s.persistent_id === dropTarget.persistent_id,
      );
      if (dropTargetIndex >= 0) {
        return dropTargetIndex;
      }
      // Fallback: insert at position 0
      return 0;
    }

    // position === 'child'
    // Insert as a child: insert at the end
    return siblings.length;
  }

  /**
   * Updates an item's properties in the items array.
   * This ensures that updates are reflected in the service's state and propagated to all subscribers.
   *
   * @param item - The item to update (matched by persistent_id)
   * @param patch - Partial item data to update
   */
  updateItem(item: QuoteItemData, patch: Partial<QuoteItemData>): void {
    const items = this.items().map((i) =>
      i.persistent_id === item.persistent_id ? { ...i, ...patch } : i,
    );
    this._items.set(items);
  }

  /**
   * Creates a blank item and adds it to the tree
   */
  createBlankItem(
    name: string = '',
    parentId: string | null = null,
  ): QuoteItemData {
    const persistentId = this.generatePersistentId();
    const currentItems = this.items();

    const siblings = currentItems.filter((item) => item.parent_id === parentId);
    // Use consecutive ordering: 0, 1, 2, 3...
    const siblingOrder = siblings.length;

    const newItem: QuoteItemData = {
      persistent_id: persistentId,
      name: name,
      parent_id: parentId,
      sibling_order: siblingOrder,
      cad_object_ids: [],
      capex_equipment_ids: [],
      consumables: [],
      source: 'created',
    };

    const updatedItems = [...currentItems, newItem];
    this._items.set(updatedItems);

    return newItem;
  }

  addBlueprintItem(
    blueprint: Blueprint,
    parentId: string | null = null,
  ): QuoteItemData {
    const currentItems = this.items();
    const siblings = currentItems.filter((item) => item.parent_id === parentId);

    const newItem: QuoteItemData = {
      persistent_id: this.generatePersistentId(),
      name: blueprint.name,
      blueprint_id: blueprint.id,
      material_id: blueprint.default_material_id ?? null,
      inherit_material: false,
      parent_id: parentId,
      sibling_order: siblings.length,
      cad_object_ids: [],
      capex_equipment_ids: [],
      consumables: [],
      source: 'loaded',
    };

    const updatedItems = [...currentItems, newItem];

    this._items.set(updatedItems);
    return newItem;
  }

  /**
   * Removes an item and all its children recursively
   */
  removeItem(itemPersistentId: string): void {
    const currentItems = this.items();
    const idsToRemove = new Set<string>();
    idsToRemove.add(itemPersistentId);

    const findChildren = (parentId: string): void => {
      const children = currentItems.filter(
        (item) => item.parent_id === parentId,
      );
      children.forEach((child) => {
        idsToRemove.add(child.persistent_id);
        findChildren(child.persistent_id);
      });
    };

    findChildren(itemPersistentId);

    const updatedItems = currentItems.filter(
      (item) => !idsToRemove.has(item.persistent_id),
    );

    this._items.set(updatedItems);
  }

  /**
   * Clears all items
   */
  clearItems(): void {
    this._items.set([]);
  }

  /**
   * Generates a unique persistent ID
   */
  private generatePersistentId(): string {
    const s4 = () =>
      Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    return `local-${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  }
}
